import { FunctionComponent, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Div, GridBag, Page, SideNavigation, Flex, Headline, Button, Divider, Title, BodyText, Loader } from '@gaiads/telia-react-component-library';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { cloneDeep, pick } from 'lodash';
import { useHistory } from 'react-router';
import { SideNavigationItemType } from '@gaiads/telia-react-component-library/build/types/components/SideNavigation/SideNavigationItem/SideNavigationItem';
import { UserRole, Person, Address } from '@cdaas/api-clients/dist/asset-user-openapi';
import { COUNTRIES, VERIFY_POSTAL_CODE } from '@cdaas/reusable-components-daas';

import GroupsUsersCard from '../../../Components/GroupsUsersCard/GroupsUsersCard';
import {
  getUserGroups,
  selectUserGroup,
  deleteUserGroup,
  modalShow,
  modalClose,
  getUserGroupUsers,
  showGrowlNotificationTranslated,
  getAllUsers,
  saveUserGroup,
} from '../../../store/actions';
import UserGroupEdit from '../../../Components/UserGroupEdit/UserGroupEdit';
import { RootState } from '../../../store/reducers';
import OrganizationSetupHeader from '../OrganizationSetupHeader/OrganizationSetupHeader';
import { hasAddressRequiredFields } from '../../../utils';
import { User, UserGroup } from '../../../types';

import classes from './GroupSettings.module.css';

export interface UserGroupEditForm {
  default_user_roles: UserRole[];
  delivery: Omit<Address, 'name'>;
  billing?: Omit<Address, 'name'> | Person;
}

const GroupSettings: FunctionComponent = () => {
  const userGroups: UserGroup[] = useSelector((state: RootState) => state.userGroups.userGroups, shallowEqual);
  const users: User[] = useSelector((state: RootState) => state.users.users, shallowEqual);
  const userGroupUsers: User[] = useSelector((state: RootState) => state.users.userGroupUsers, shallowEqual);
  const isAuthenticated: boolean = useSelector((state: RootState) => state.authentication.authenticated);
  const selectedUserGroup: UserGroup | undefined = useSelector((state: RootState) => state.userGroups.selectedUserGroup, shallowEqual);
  const organization = useSelector((state: RootState) => state.authentication.user?.organization);
  const selectedOrganization = useSelector((state: RootState) => state.authentication.selectedOrganization);

  const [loading, setLoading] = useState<boolean>(true);

  const { t } = useTranslation('onboarding');
  const { t: tGlobal } = useTranslation('globalComponents');
  const dispatch = useDispatch();
  const history = useHistory();

  const [groupsUsers, setGroupsUsers] = useState<User[]>([]);
  const [firstUserGroupSelectedOnInitialRender, setFirstUserGroupSelectedOnInitialRender] = useState<boolean>(false);

  const [usersAreModified, setUsersAreModified] = useState<boolean>(false);

  const allCountries = COUNTRIES.map(cc => ({
    countryName: tGlobal(`countries.${cc.countryName}`),
    value: cc.value,
  }));

  const defaultValues: UserGroupEditForm = {
    delivery: {
      address1: '',
      address2: '',
      street: '',
      city: '',
      zip: '',
      country: '',
    },
    default_user_roles: [],
  };

  const { handleSubmit, ...formUtils } = useForm<UserGroupEditForm>({ defaultValues });

  formUtils.register('default_user_roles', {
    validate: {
      length: (value: UserRole[]) => (value?.length < 1 ? `${tGlobal('UserGroupEdit.errorTextRoles')}` : undefined),
    },
  });
  formUtils.register('delivery.country');

  useEffect(() => {
    if (userGroupUsers) {
      setGroupsUsers(userGroupUsers);
    }
  }, [userGroupUsers]);

  const resetUserGroupEditForm = (): void => {
    const deliveryCountry = allCountries.find(
      country =>
        country.countryName === selectedUserGroup?.default_delivery_address?.country ||
        country.value === selectedUserGroup?.default_delivery_address?.country,
    )?.value;

    formUtils.reset({
      delivery: {
        address1: selectedUserGroup?.default_delivery_address?.address1 || '',
        address2: selectedUserGroup?.default_delivery_address?.address2 || '',
        street: selectedUserGroup?.default_delivery_address?.street || '',
        city: selectedUserGroup?.default_delivery_address?.city || '',
        country: deliveryCountry || '',
        zip: selectedUserGroup?.default_delivery_address?.zip || '',
      },
      default_user_roles: selectedUserGroup?.default_user_roles || [],
    });

    formUtils.clearErrors();
  };

  const isAddressValidWithSetErrors = (formData: UserGroupEditForm) => {
    const delivery = formData.delivery;
    if (delivery.street || delivery.address1 || delivery.address2 || delivery.zip || delivery.city || delivery.country) {
      if (!(delivery.street && delivery.zip && delivery.city && delivery.country)) {
        if (!delivery.street) {
          formUtils.setError('delivery.street', { message: tGlobal('UserGroupEdit.errorTextRequired') });
        }
        if (!delivery.zip) {
          formUtils.setError('delivery.zip', { message: tGlobal('UserGroupEdit.errorTextRequired') });
        }
        if (!delivery.city) {
          formUtils.setError('delivery.city', { message: tGlobal('UserGroupEdit.errorTextRequired') });
        }
        if (!delivery.country) {
          formUtils.setError('delivery.country', { message: tGlobal('UserGroupEdit.errorTextRequired') });
        }
        return false;
      }
    }
    if (delivery.zip && !delivery.zip.match(VERIFY_POSTAL_CODE)) {
      formUtils.setError('delivery.zip', { message: `${tGlobal('UserGroupEdit.errorTextPleaseEnter')} ${tGlobal('UserGroupEdit.zip')}` });
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getUserGroups());
      dispatch(getAllUsers());
    }
  }, [isAuthenticated, dispatch]);

  useEffect(() => {
    if (userGroups.length && !firstUserGroupSelectedOnInitialRender) {
      dispatch(selectUserGroup(userGroups[0].id!));
      setFirstUserGroupSelectedOnInitialRender(true);
    }
  }, [userGroups, dispatch, firstUserGroupSelectedOnInitialRender]);

  useEffect(() => {
    if (selectedUserGroup) {
      dispatch(getUserGroupUsers(selectedUserGroup.id!))
        .then(() => {
          resetUserGroupEditForm();
          setUsersAreModified(false);
        })
        .finally(() => setLoading(false));
    }
    // used functions not added as dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserGroup, users, userGroups]);

  const handleCancelButton = (): void => {
    setGroupsUsers(userGroupUsers);
    resetUserGroupEditForm();
    setUsersAreModified(false);
  };

  const handleSaveButton = async (selectGroupAfterSaving: boolean): Promise<void> => {
    // Clear manually set errors if they exist before running validation
    formUtils.clearErrors('delivery');
    handleSubmit(async data => {
      // Second round of validation of address fields
      if (!isAddressValidWithSetErrors(data)) {
        return;
      }
      const newState = cloneDeep(selectedUserGroup!);
      newState.default_user_roles = data.default_user_roles;
      newState.default_delivery_address = {
        ...newState.default_delivery_address!,
        ...pick(data.delivery, ['address1', 'address2', 'street', 'city', 'zip', 'country']),
      };
      // Do not allow empty strings as address
      if (!hasAddressRequiredFields(newState.default_delivery_address)) {
        delete newState.default_delivery_address;
      }
      dispatch(saveUserGroup(newState, selectGroupAfterSaving));
    })();
  };

  const handleGroupSelection = (id: string): void => {
    setLoading(true);
    dispatch(selectUserGroup(id));
  };

  const handleDeleteGroup = (): void => {
    dispatch(deleteUserGroup(selectedUserGroup!));
    dispatch(modalClose());
  };

  const onClickDeleteUserGroup = (): void => {
    dispatch(
      modalShow(
        t('groupSettingsPage.deleteModal.title'),
        t('groupSettingsPage.deleteModal.confirmButton'),
        t('groupSettingsPage.deleteModal.cancelButton'),
        handleDeleteGroup,
        () => dispatch(modalClose()),
        <BodyText>
          {t('groupSettingsPage.deleteModal.content')} {selectedUserGroup?.name}
        </BodyText>,
      ),
    );
  };

  const noChangesInForm = !formUtils.formState.isDirty && !usersAreModified;

  const navigationItems: SideNavigationItemType[] = userGroups.map<SideNavigationItemType>(ug => ({
    label: ug.name,
    active: ug.id === selectedUserGroup?.id,
    onClick: async () => {
      if (!noChangesInForm) {
        if (!isAddressValidWithSetErrors(formUtils.getValues())) {
          return;
        }
        await handleSaveButton(false);
      }
      if (ug.id !== selectedUserGroup?.id) {
        handleGroupSelection(ug.id!);
      }
    },
    key: ug.id,
  }));

  const validateAndSaveSettings = () => {
    if (!noChangesInForm) {
      return dispatch(showGrowlNotificationTranslated('onboarding:groupSettingsPage.notReadyToFinish', 'warning'));
    }
    history.push('/onboarding/approval');
  };

  return (
    <Page>
      <OrganizationSetupHeader onboardingCurrentStepIndex={2} inputData={userGroups} validateAndSaveOnboardingStepData={validateAndSaveSettings} />
      <Flex>
        <SideNavigation className={classes.sidenavigation} navigationItems={navigationItems} padding={{ top: 'xlg' }} />
        {loading && (
          <Flex margin={{ top: 'xlg' }} centered occupyHorizontalSpace>
            <Loader />
          </Flex>
        )}
        {!loading && !!selectedUserGroup && (
          <Div>
            <Flex occupyVerticalSpace={false} centeredVertically spaceBetween margin={{ left: 'xlg', vertical: 'md' }}>
              <Headline size={'md'}>{selectedUserGroup.name}</Headline>
              <Div>
                {!noChangesInForm && (
                  <Button onClick={handleCancelButton} margin={{ right: 'md' }} variant="text" size="lg">
                    {t('groupSettingsPage.cancelButton')}
                  </Button>
                )}
                <Button onClick={() => handleSaveButton(true)} disabled={noChangesInForm}>
                  {t('groupSettingsPage.saveButton')}
                </Button>
              </Div>
            </Flex>
            <Div margin={{ left: 'xlg' }}>
              <Divider />
            </Div>
            <Div margin={{ size: 'xlg' }}>
              <GridBag>
                <GridBag.Item md={12}>
                  <Title margin={{ size: 'sm' }} size={'sm'}>
                    {t('groupSettingsPage.basicInformationTitle')}
                  </Title>
                  <Div className={classes['form-width']} boxShadow={'sm'} borderRadius={{ size: 'md' }}>
                    <UserGroupEdit companyName={selectedOrganization?.name || organization?.name || ''} formUtils={formUtils} />
                  </Div>
                </GridBag.Item>
                <GridBag.Item md={12}>
                  <Title margin={{ size: 'sm' }} size={'sm'}>
                    {t('groupSettingsPage.groupMembersTitle')}
                  </Title>
                  <Div showOverflow boxShadow={'sm'} borderRadius={{ size: 'md' }} backgroundColor="white">
                    <GroupsUsersCard users={groupsUsers} setUsers={setGroupsUsers} setUsersAreModified={setUsersAreModified} />
                  </Div>
                </GridBag.Item>
                <GridBag.Item md={12}>
                  <Button variant="destructive" onClick={onClickDeleteUserGroup}>
                    {t('groupSettingsPage.deleteButton')}
                  </Button>
                </GridBag.Item>
              </GridBag>
            </Div>
          </Div>
        )}
      </Flex>
    </Page>
  );
};

export default GroupSettings;
